import React from 'react';

import Insight from '../../models/Insight/Insight.js';
import FooterCta from "../../models/FooterCta/FooterCta";
import Quote from '../../models/Quote/Quote';
import POVBlock from '../../models/POVBlock/POVBlock';

import JsonLd from '../../components/jsonld.jsx';
import SEO from '../../components/seo.jsx';
import ClientQuote from '../../components/client-quote.jsx';
import InternalLinkSection from '../../components/internal-link-section.jsx';
import RelatedInsights from '../../components/related-insights.jsx';
import FooterCTA from '../../components/contact-us/footer-cta.jsx';
import PointOfViewSection from '../../components/point-of-view-section.jsx';
import renderHeroSection from '../../components/about-us/hero-section';
import Layout from '../../components/layout.jsx';

import { Paper } from '../../ui-components';

/**
 * Renders the SEO metatag and structured data elements
 *
 * @function renderSEO
 * 
 * @param {object} CONTENT
 * @param {object} data
 * 
 * @returns {JSX}
 */

const renderSEO = (CONTENT, data) => {
  const image = data?.heroImage1?.childImageSharp?.gatsbyImageData;

  return (
    <>
      <SEO
        title={CONTENT.SEO_TITLE}
        description={CONTENT.SEO_DESCRIPTION}
        slug={CONTENT.SEO_SLUG}
        image={image?.gatsbyImageData?.images?.fallback?.src}
      />
      <JsonLd
        item={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: CONTENT.SEO_TITLE,
          description: CONTENT.SEO_SUBTITLE,
          inLanguage: 'en',
          url: `http://credera.com/${CONTENT.SEO_SLUG}`,
          isAccessibleForFree: true,
          image: image?.gatsbyImageData?.images?.fallback?.src
          // significantLink: TODO - Add link to job openings
        }}
      />
    </>
  );
};

const renderFeaturedQuoteSection = featuredQuote => {
  return (
      <ClientQuote quote={featuredQuote} layout={'side-by-side'} />
  );
};

const renderPovSection = (povDetail, geoSpace) => {

  return (
    <PointOfViewSection pointOfView={povDetail} geoSpace={geoSpace} />
  );
};

const renderErgCards = (data, CONTENT) => {
  let ergInternalCard = [];

  data.forEach(erg => {
    ergInternalCard.push({
      title: erg?.node?.ergTitle,
      slug: erg?.node?.slug,
      content: erg?.node?.snippet?.snippet,
      image: erg?.node?.ergHeroImages[0]?.gatsbyImageData
    });
  });

  return (
    <InternalLinkSection
      label={CONTENT.ERG__CARDS__SECTION__LABEL}
      title={CONTENT.ERG__CARDS__SECTION__TITLE}
      content={CONTENT.ERG__CARDS__SECTION__BODY}
      blocks={ergInternalCard}
      headingWidth={800}
    />
  );
};

const renderRelatedInsights = data => {
  const relatedInsight1 = new Insight(data?.relatedInsight1?.edges[0]?.node);
  const relatedInsight2 = new Insight(data?.relatedInsight2?.edges[0]?.node);
  const relatedInsight3 = new Insight(data?.relatedInsight3?.edges[0]?.node);

  return <RelatedInsights insights={[relatedInsight1, relatedInsight2, relatedInsight3]} id="insights" layout={'side-by-side'} />;
};

const renderErgPage = (CONTENT, data, geoSpace) => {
  const ergDetails = data?.allContentfulErgDetails?.edges;
  const featuredQuote = new Quote(data?.featuredQuote?.edges[0]?.node);
  const povDetail = new POVBlock(data?.povSection?.edges[0]?.node);
  const footerCta = new FooterCta(data?.footerCta);

  return (
    <Layout>
      {renderSEO(CONTENT, data)}
      <Paper bottom>
        {renderHeroSection(
          CONTENT.TITLE,
          CONTENT.BODY,
          CONTENT.LABEL,
          [data.heroImage1.childImageSharp, data.heroImage2.childImageSharp],
          'core'
        )}
        {featuredQuote && renderFeaturedQuoteSection(featuredQuote)}
      </Paper>
      {renderPovSection(povDetail, geoSpace)}
      <Paper top>
        {renderErgCards(ergDetails, CONTENT)}
        {renderRelatedInsights(data)}
      </Paper>
      {footerCta && <FooterCTA footerCta={footerCta} />}
    </Layout>
  );
};

export { 
  renderFeaturedQuoteSection,
  renderPovSection,
  renderRelatedInsights,
  renderErgPage
};
